import React from 'react';
import { FileText, Clock, Sparkles } from 'lucide-react';

interface DocumentHoverPreviewProps {
  filename: string;
  summary?: string;
  size?: number;
  modified?: string;
  position: { x: number; y: number };
}

export default function DocumentHoverPreview({
  filename,
  summary,
  size,
  modified,
  position
}: DocumentHoverPreviewProps) {
  const hasSummary = summary && summary.length > 0;

  // Formater la taille
  const formatSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Formater la date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Couleur selon extension
  const getTypeInfo = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'md') return { label: 'Markdown', color: 'bg-sky-500' };
    if (ext === 'pdf') return { label: 'PDF', color: 'bg-red-500' };
    if (ext === 'json') return { label: 'JSON', color: 'bg-amber-500' };
    if (ext === 'txt') return { label: 'Texte', color: 'bg-gray-500' };
    if (['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext || '')) return { label: 'Image', color: 'bg-purple-500' };
    return { label: 'Fichier', color: 'bg-gray-400' };
  };

  const typeInfo = getTypeInfo(filename);

  return (
    <div
      className="fixed z-[100] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-none animate-fadeIn"
      style={{
        left: Math.min(position.x + 20, window.innerWidth - 340),
        top: Math.max(10, Math.min(position.y - 60, window.innerHeight - 200)),
        width: '320px',
      }}
    >
      {/* Header avec type badge */}
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 ${typeInfo.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-900 truncate text-sm">
              {filename.replace(/\.[^/.]+$/, '')}
            </h4>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${typeInfo.color}`}>
                {typeInfo.label}
              </span>
              {size && (
                <span className="text-[10px] text-gray-400">{formatSize(size)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="px-4 py-3">
        {hasSummary ? (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">
                Résumé IA
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {summary}
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">
            Aucun résumé disponible pour ce document.
          </p>
        )}
      </div>

      {/* Footer métadonnées */}
      {modified && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
            <Clock className="w-3 h-3" />
            <span>Modifié le {formatDate(modified)}</span>
          </div>
        </div>
      )}

      {/* Flèche */}
      <div
        className="absolute w-3 h-3 bg-white border-l border-t border-gray-100 transform -rotate-45"
        style={{
          left: -6,
          top: 24,
        }}
      />
    </div>
  );
}
